// plan-13 S01: deterministic seeding utilities — UTC date/minute, djb2, Mulberry32, broadcast schedule

export function utcDate(): string {
  const d = new Date()
  const y = d.getUTCFullYear()
  const m = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function utcMinute(): string {
  const d = new Date()
  const y = d.getUTCFullYear()
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0')
  const day = String(d.getUTCDate()).padStart(2, '0')
  const hh = String(d.getUTCHours()).padStart(2, '0')
  const mm = String(d.getUTCMinutes()).padStart(2, '0')
  return `${y}-${mo}-${day}T${hh}:${mm}Z`
}

export function fromString(s: string): number {
  // djb2 — same algorithm as heatmap's strHash
  let h = 5381
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h) ^ s.charCodeAt(i)
    h = h >>> 0
  }
  return h
}

export function mulberry32(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s += 0x6D2B79F5
    let t = Math.imul(s ^ (s >>> 15), 1 | s)
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const BROADCAST_WINDOW_MS = 288_000 // 4m48s

export interface BroadcastTiming {
  dateKey: string
  isBroadcastWindow: boolean
  msUntilNext: number
  msSinceStart: number
}

export function broadcastDate(): BroadcastTiming {
  const now = Date.now()
  const d = new Date(now)

  // today's date key
  const dateKey = (() => {
    const y = d.getUTCFullYear()
    const m = String(d.getUTCMonth() + 1).padStart(2, '0')
    const day = String(d.getUTCDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  })()

  // ms since midnight UTC
  const msSinceMidnight =
    d.getUTCHours() * 3600000 +
    d.getUTCMinutes() * 60000 +
    d.getUTCSeconds() * 1000 +
    d.getUTCMilliseconds()

  const windowStart = 4 * 3600000 + 48 * 60000 // 04:48:00 in ms = 17280_000
  const windowEnd = windowStart + BROADCAST_WINDOW_MS // 04:52:48 = 17760_000

  // ms since window opened (negative if before window)
  const msSinceStart = msSinceMidnight - windowStart
  const isBroadcastWindow = msSinceStart >= 0 && msSinceStart <= BROADCAST_WINDOW_MS

  // suppress unused warning — windowEnd documents the end of the broadcast window
  void windowEnd

  // ms until next 04:48 UTC
  let msUntilNext: number
  if (msSinceMidnight < windowStart) {
    msUntilNext = windowStart - msSinceMidnight
  } else if (isBroadcastWindow) {
    msUntilNext = 0 // currently in window
  } else {
    // past today's window — next is tomorrow's
    msUntilNext = (86400000 - msSinceMidnight) + windowStart
  }

  return {
    dateKey,
    isBroadcastWindow,
    msUntilNext,
    msSinceStart: isBroadcastWindow ? msSinceStart : -1,
  }
}
